import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface Classif {
  id: string;
  name: string;
}

interface Tier {
  id: string;
  category_id: string;
  min_amount: number;
  max_amount: number | null;
  markup_percentage: number;
  created_at: string;
  updated_at: string;
}

const formatCurrency = (n: number) =>
  `R ${Number(n).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function SystemMarkupTiers() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const adminId = session?.user?.id ?? null;

  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

  const classifQuery = useQuery({
    queryKey: ["smt-classif"],
    queryFn: async (): Promise<Classif[]> => {
      const { data, error } = await supabase
        .from("supplier_categories")
        .select("id, name")
        .eq("is_default", true)
        .order("name");
      if (error) throw error;
      return (data ?? []) as Classif[];
    },
  });

  const tiersQuery = useQuery({
    queryKey: ["system-default-markup-tiers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("system_default_markup_tiers" as never)
        .select("*, supplier_categories!category_id(id, name)")
        .order("category_id")
        .order("min_amount");
      if (error) throw error;
      return (data ?? []) as unknown as Tier[];
    },
  });

  useEffect(() => {
    const data = classifQuery.data;
    if (data && data.length > 0 && selectedCategoryId === null) {
      setSelectedCategoryId(data[0].id);
    }
  }, [classifQuery.data, selectedCategoryId]);

  useEffect(() => {
    const channel = supabase
      .channel("admin-system-markup-tiers")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "system_default_markup_tiers" },
        () => {
          queryClient.invalidateQueries({ queryKey: ["system-default-markup-tiers"] });
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const classifs = classifQuery.data ?? [];
  const tiers = tiersQuery.data ?? [];

  const tiersForSelected = useMemo(
    () => tiers.filter((t) => t.category_id === selectedCategoryId),
    [tiers, selectedCategoryId]
  );

  // Sheet state
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<Tier | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [minAmount, setMinAmount] = useState("0");
  const [maxAmount, setMaxAmount] = useState("");
  const [markupPct, setMarkupPct] = useState("0");
  const [errors, setErrors] = useState<{ min?: string; max?: string; markup?: string }>({});

  // Delete confirm popover state
  const [deleteOpenId, setDeleteOpenId] = useState<string | null>(null);

  const openCreate = () => {
    setEditing(null);
    setMinAmount("0");
    setMaxAmount("");
    setMarkupPct("0");
    setErrors({});
    setSheetOpen(true);
  };

  const openEdit = (tier: Tier) => {
    setEditing(tier);
    setMinAmount(String(tier.min_amount));
    setMaxAmount(tier.max_amount === null ? "" : String(tier.max_amount));
    setMarkupPct(String(tier.markup_percentage));
    setErrors({});
    setSheetOpen(true);
  };

  const writeAudit = async (
    action:
      | "system_markup_tier_created"
      | "system_markup_tier_updated"
      | "system_markup_tier_deleted",
    entityId: string,
    details: Record<string, unknown>
  ) => {
    if (!adminId) return;
    await supabase.from("admin_audit_logs").insert({
      admin_id: adminId,
      action,
      entity_type: "system_default_markup_tiers",
      entity_id: entityId,
      details: details as never,
    });
  };

  const validate = (): boolean => {
    const errs: { min?: string; max?: string; markup?: string } = {};
    const min = Number(minAmount);
    const max = maxAmount.trim() === "" ? null : Number(maxAmount);
    const mk = Number(markupPct);

    if (Number.isNaN(min) || min < 0) errs.min = "Must be 0 or greater";
    if (max !== null && Number.isNaN(max)) errs.max = "Invalid number";
    if (max !== null && !Number.isNaN(max) && max <= min)
      errs.max = "Must be greater than min amount";
    if (Number.isNaN(mk) || mk < 0) errs.markup = "Must be 0 or greater";
    if (!Number.isNaN(mk) && mk > 100) errs.markup = "Must be 100 or less";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!selectedCategoryId) {
      toast.error("Select a category first");
      return;
    }
    if (!validate()) return;

    const min = Number(minAmount);
    const max = maxAmount.trim() === "" ? null : Number(maxAmount);
    const mk = Number(markupPct);

    setSubmitting(true);
    try {
      if (editing) {
        const { error } = await supabase
          .from("system_default_markup_tiers" as never)
          .update({
            min_amount: min,
            max_amount: max,
            markup_percentage: mk,
            updated_by: adminId,
          } as never)
          .eq("id", editing.id)
          .select();
        if (error) throw error;
        await writeAudit("system_markup_tier_updated", editing.id, {
          category_id: editing.category_id,
          min_amount: min,
          max_amount: max,
          markup_percentage: mk,
        });
        toast.success("Tier updated");
      } else {
        const { data, error } = await supabase
          .from("system_default_markup_tiers" as never)
          .insert({
            category_id: selectedCategoryId,
            min_amount: min,
            max_amount: max,
            markup_percentage: mk,
            created_by: adminId,
            updated_by: adminId,
          } as never)
          .select()
          .single();
        if (error) throw error;
        const newTier = data as unknown as Tier;
        await writeAudit("system_markup_tier_created", newTier.id, {
          category_id: selectedCategoryId,
          min_amount: min,
          max_amount: max,
          markup_percentage: mk,
        });
        toast.success("Tier created");
      }
      setSheetOpen(false);
      queryClient.invalidateQueries({ queryKey: ["system-default-markup-tiers"] });
    } catch (err) {
      const e = err as { code?: string; message?: string };
      if (e?.code === "23P01") {
        toast.error("This range overlaps with an existing tier for this category.");
      } else {
        toast.error(e?.message || "Failed to save");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (tier: Tier) => {
    try {
      const { error } = await supabase
        .from("system_default_markup_tiers" as never)
        .delete()
        .eq("id", tier.id);
      if (error) throw error;
      await writeAudit("system_markup_tier_deleted", tier.id, {
        category_id: tier.category_id,
      });
      toast.success("Tier deleted");
      setDeleteOpenId(null);
      queryClient.invalidateQueries({ queryKey: ["system-default-markup-tiers"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete");
    }
  };

  return (
    <>
      <div className="flex gap-4">
        {/* Left: category list */}
        <div className="w-64 shrink-0 bg-slate-900 border border-slate-800 rounded-md overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-800 text-xs uppercase tracking-wider text-slate-400">
            Categories
          </div>
          <div className="py-1">
            {classifQuery.isLoading ? (
              <div className="px-3 py-4 text-sm text-slate-400 flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading...
              </div>
            ) : classifs.length === 0 ? (
              <div className="px-3 py-4 text-sm text-slate-400">No categories</div>
            ) : (
              <ul className="space-y-0.5">
                {classifs.map((c) => {
                  const active = c.id === selectedCategoryId;
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedCategoryId(c.id)}
                        className={[
                          "w-full text-left px-3 py-1.5 text-sm border-l-2 transition-colors",
                          active
                            ? "bg-slate-800 border-amber-500 text-white"
                            : "border-transparent text-slate-300 hover:bg-slate-800/60",
                        ].join(" ")}
                      >
                        {c.name}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Right: tiers panel */}
        <div className="flex-1 bg-slate-900 border border-slate-800 rounded-md">
          {!selectedCategoryId ? (
            <div className="p-8 text-center text-sm text-slate-400">
              Select a category to view its tiers
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
                <div className="text-sm font-medium text-white">
                  {classifs.find((c) => c.id === selectedCategoryId)?.name} Tiers
                </div>
                <Button
                  onClick={openCreate}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-950"
                  size="sm"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Tier
                </Button>
              </div>
              {tiersQuery.isLoading ? (
                <div className="p-6 text-sm text-slate-400 flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading...
                </div>
              ) : tiersForSelected.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-400">
                  No tiers configured for this category.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-800">
                      <th className="px-4 py-2 text-left text-xs uppercase tracking-wider text-slate-400 font-medium">
                        Min Amount
                      </th>
                      <th className="px-4 py-2 text-left text-xs uppercase tracking-wider text-slate-400 font-medium">
                        Max Amount
                      </th>
                      <th className="px-4 py-2 text-left text-xs uppercase tracking-wider text-slate-400 font-medium">
                        Markup %
                      </th>
                      <th className="px-4 py-2 text-right text-xs uppercase tracking-wider text-slate-400 font-medium w-32">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tiersForSelected.map((t) => (
                      <tr
                        key={t.id}
                        className="bg-slate-900 hover:bg-slate-800/50 border-b border-slate-800/60"
                      >
                        <td className="px-4 py-2 text-slate-200">{formatCurrency(t.min_amount)}</td>
                        <td className="px-4 py-2 text-slate-200">
                          {t.max_amount === null ? (
                            <span className="text-slate-400 italic">No limit</span>
                          ) : (
                            formatCurrency(t.max_amount)
                          )}
                        </td>
                        <td className="px-4 py-2 text-slate-200">
                          {Number(t.markup_percentage).toFixed(2)}%
                        </td>
                        <td className="px-4 py-2 text-right">
                          <div className="inline-flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-slate-300 hover:text-white hover:bg-slate-800"
                              onClick={() => openEdit(t)}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Popover
                              open={deleteOpenId === t.id}
                              onOpenChange={(o) => setDeleteOpenId(o ? t.id : null)}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-red-400 hover:text-red-300 hover:bg-slate-800"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent
                                align="end"
                                className="w-64 bg-slate-900 border-slate-700 text-slate-100"
                              >
                                <div className="space-y-3">
                                  <div className="text-sm font-medium text-white">
                                    Delete this tier?
                                  </div>
                                  <div className="text-xs text-slate-400">
                                    This action cannot be undone.
                                  </div>
                                  <div className="flex justify-end gap-2">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => setDeleteOpenId(null)}
                                      className="text-slate-300 hover:text-white hover:bg-slate-800"
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={() => handleDelete(t)}
                                      className="bg-red-600 hover:bg-red-700 text-white"
                                    >
                                      Delete
                                    </Button>
                                  </div>
                                </div>
                              </PopoverContent>
                            </Popover>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>

      <Dialog open={sheetOpen} onOpenChange={setSheetOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editing ? "Edit Tier" : "Add Tier"}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {classifs.find((c) => c.id === selectedCategoryId)?.name ?? ""}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-slate-300">Min Amount</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                  R
                </span>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={minAmount}
                  onChange={(e) => setMinAmount(e.target.value)}
                  className="pl-7 bg-slate-800 border-slate-700 text-white"
                />
              </div>
              {errors.min && <p className="text-xs text-red-400">{errors.min}</p>}
            </div>

            <div className="space-y-1.5">
              <Label className="text-slate-300">
                Max Amount{" "}
                <span className="text-slate-500 text-xs">(blank = no upper limit)</span>
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                  R
                </span>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={maxAmount}
                  onChange={(e) => setMaxAmount(e.target.value)}
                  placeholder="No limit"
                  className="pl-7 bg-slate-800 border-slate-700 text-white"
                />
              </div>
              {errors.max && <p className="text-xs text-red-400">{errors.max}</p>}
            </div>

            <div className="space-y-1.5">
              <Label className="text-slate-300">Markup %</Label>
              <Input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                max="100"
                value={markupPct}
                onChange={(e) => setMarkupPct(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white"
              />
              {errors.markup && <p className="text-xs text-red-400">{errors.markup}</p>}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setSheetOpen(false)}
              className="text-slate-300 hover:text-white hover:bg-slate-800"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-amber-500 hover:bg-amber-600 text-slate-950"
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
